import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { qk } from "../../lib/queryKeys";
import { getJson } from "../../lib/http";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useVehiclesSocketStore } from "../../store/admin/vehiclesSocketStore";
import { useUsersStore, type User } from "../../store/admin/usersStore";
import {
  useDriversStore,
  type AvailableUser,
} from "../../store/admin/driversStore";
import {
  parseNotificationPrefsResponse,
} from "./notificationPrefs";

interface ListResponse<T> {
  data: T[];
}

interface ListMeta {
  error: string | null;
  count: number;
  status: number;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  url: string;
}

export interface UserListParams {
  page?: number;
  limit?: number;
  status_id?: string;
  search?: string;
}

export interface UserListResult {
  data: User[];
  meta?: ListMeta;
}

const ALL_USERS_LIMIT = 1000;

export function useUsersList(params: UserListParams = {}) {
  const cid = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.users.list(cid, params),
    queryFn: ({ signal }) =>
      getJson<UserListResult>(
        "/userList",
        { page: 1, limit: 9, ...params },
        { signal },
      ).then((body) => ({ data: body.data ?? [], meta: body.meta })),
  });
}

function mapAvailableUsers(body: unknown): AvailableUser[] {
  const payload = (body as { data?: unknown })?.data ?? body;
  const raw = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as { data?: unknown })?.data)
      ? (payload as { data: unknown[] }).data
      : [];
  return (raw as Record<string, any>[]).map((user) => ({
    id: user.id,
    firstName: user.first_name || user.firstName || "",
    lastName: user.last_name || user.lastName || "",
    email: user.email || "",
    username: user.username || "",
    profileImg: user.profileImg || user.profile_img || "",
  }));
}

export interface UseAvailableUsersOptions {
  currentDriverId?: string;
  enabled?: boolean;
}

export function useAvailableUsers({
  currentDriverId,
  enabled = true,
}: UseAvailableUsersOptions = {}) {
  const companyId = useCompaniesStore((s) => s.companyId);
  const selectedCompany = useVehiclesSocketStore((s) => s.selectedCompany);
  const cid = selectedCompany || companyId;

  const query = useQuery({
    queryKey: qk.availableUsers(cid, currentDriverId),
    queryFn: ({ signal }) =>
      getJson<unknown>(
        "/availableUsers",
        currentDriverId ? { currentDriverId } : {},
        { signal },
        { headers: { "x-company-id": cid ?? "" } },
      ).then(mapAvailableUsers),
    enabled,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (query.data) {
      useDriversStore.setState({ availableUsers: query.data });
    }
  }, [query.data]);

  return query;
}

export function useAllUsers(allCompanies = false) {
  const companyId = useCompaniesStore((s) => s.companyId);
  const companyUser = useCompaniesStore((s) => s.companyUser);
  const selectedCompany = useVehiclesSocketStore((s) => s.selectedCompany);
  const cid = selectedCompany || companyId || companyUser;

  const query = useQuery({
    queryKey: qk.users.all(cid, allCompanies),
    queryFn: ({ signal }) =>
      getJson<ListResponse<User>>(
        "/userList",
        {
          limit: ALL_USERS_LIMIT,
          page: 1,
          ...(allCompanies ? {} : { company_id: cid ?? "" }),
        },
        { signal },
        allCompanies
          ? ({ _skipCompanyScope: true } as never)
          : { headers: { "x-company-id": cid ?? "" } },
      ).then((res) => res.data ?? []),
    enabled: allCompanies || !!cid,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (query.data) {
      useUsersStore.setState({ users: query.data });
    }
  }, [query.data]);

  return query;
}

export function useResellerOrAdminUsers(roleId: string | null | undefined) {
  const cid = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.users.resellerOrAdmin(cid, roleId ?? null),
    queryFn: ({ signal }) =>
      getJson<ListResponse<User>>(
        "/usersByResellerOrAdmin",
        { roleId: roleId! },
        { signal },
      ).then((res) => res.data ?? []),
    enabled: !!roleId,
    staleTime: 5 * 60_000,
  });
}

export function useUserNotificationPrefs(userId: string | null | undefined) {
  return useQuery({
    queryKey: qk.users.notificationPrefs(userId ?? null),
    queryFn: ({ signal }) =>
      getJson<{ notification_prefs?: unknown }>(
        "/user/notification-prefs",
        { userID: userId! },
        { signal },
      ).then((body) => parseNotificationPrefsResponse(body)),
    enabled: !!userId,
    staleTime: 60_000,
  });
}
